import {
  adminVsdVars,
  defaultVsdVars,
  deleteVariableSpeedDrives,
  ecopetrolVsdVars,
  kocVsdVars,
  restoreVariableSpeedDrives
} from "@/models/equipment/vsd/predicates";
import type { Variable_Speed_Drives } from "gql/api";
import {
  useVariableSpeedDrivesQuery,
  useDeleteVsdsMutation,
  useInsertVsdMutation,
  useRestoreVsdsMutation,
  useUpdateVsdByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { vsdTableColumns } from "./vsdTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useVSDs(isAdmin: boolean): UseEquipment<Variable_Speed_Drives> {
  const baseEquipment = useEquipment<Variable_Speed_Drives>(isAdmin);
  const title = "VSDs";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocVsdVars;
    if (user.is_ecopetrol_customer) return ecopetrolVsdVars;
    if (user.is_admin) return adminVsdVars;
    return defaultVsdVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useVariableSpeedDrivesQuery({
    variables: getVariables()
  });

  const allRows = computed(
    () =>
      data.value?.variable_speed_drives as Variable_Speed_Drives[] | undefined
  );

  const hashOrder: (keyof Variable_Speed_Drives)[] = [
    "manufacturer",
    "part_number",
    "max_amps",
    "kva",
    "vsd_type",
    "enclosure"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertCasing } = useInsertVsdMutation();
  const { executeMutation: executeUpdateCasing } = useUpdateVsdByPkMutation();
  const { executeMutation: executeDeleteCasing } = useDeleteVsdsMutation();
  const { executeMutation: executeRestoreCasing } = useRestoreVsdsMutation();

  const insertVSD = (
    vsd: Omit<Variable_Speed_Drives, "sizing_import" | "user">
  ) => executeInsertCasing({ object: vsd });

  const updateVSD = (id: string, vsd: Variable_Speed_Drives) =>
    executeUpdateCasing({ id, object: vsd });

  const deleteVSD = (ids: string[]) =>
    executeDeleteCasing({ where: deleteVariableSpeedDrives(ids) });

  const restoreVSD = (ids: string[]) =>
    executeRestoreCasing({ where: restoreVariableSpeedDrives(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "variable_speed_drives",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    part_number: "",
    enclosure: "",
    vsd_type: "",
    kva: null,
    max_amps: null
  }));

  return {
    searchPlaceholder: "Search VSDs...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "1200"`,
    columns: defineVisibleColumns(vsdTableColumns),
    handleUpsert: defineUpsert(insertVSD, updateVSD),
    handleDelete: defineDelete(deleteVSD),
    handleRestore: defineRestore(restoreVSD),
    totalCount: computed(
      () => data.value?.variable_speed_drives_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: vsdTableColumns,
      globalSearchFields: ["kva", "vsd_type", "enclosure", "part_number"],
      allRows,
      title
    })
  };
}
